import React, { useEffect, useRef, useState } from 'react';
import { Menu, X, ChevronDown, ArrowRight } from 'lucide-react';
import { Link } from 'react-router-dom';

interface ServiceItem {
  title: string;
  tagline: string;
  path: string;
  image: string;
}

const SERVICE_ITEMS: ServiceItem[] = [
  {
    title: 'Travel Styling',
    tagline: 'Destination-aware outfit plans & packing lists',
    path: '/travel-styling',
    image:
      'https://images.unsplash.com/photo-1488646953014-85cb44e25828?w=600&q=80&auto=format&fit=crop',
  },
  {
    title: 'Event Styling',
    tagline: 'Weddings, galas, parties, dressed to the occasion',
    path: '/event-styling',
    image:
      'https://images.unsplash.com/photo-1492707892479-7bc8d5a4ee93?w=600&q=80&auto=format&fit=crop',
  },
  {
    title: 'Personal Styling',
    tagline: 'Capsule wardrobes & signature style discovery',
    path: '/personal-styling',
    image:
      'https://images.unsplash.com/photo-1483985988355-763728e1935b?w=600&q=80&auto=format&fit=crop',
  },
  {
    title: 'Personal Shopping',
    tagline: 'A stylist shops with you in person, select cities',
    path: '/personal-shopping',
    image:
      'https://images.unsplash.com/photo-1441986300917-64674bd600d8?w=600&q=80&auto=format&fit=crop',
  },
];

const Header: React.FC = () => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const [megaOpen, setMegaOpen] = useState(false);
  const closeTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 20);
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  const openMega = () => {
    if (closeTimerRef.current) {
      clearTimeout(closeTimerRef.current);
      closeTimerRef.current = null;
    }
    setMegaOpen(true);
  };

  const scheduleCloseMega = () => {
    if (closeTimerRef.current) clearTimeout(closeTimerRef.current);
    closeTimerRef.current = setTimeout(() => setMegaOpen(false), 120);
  };

  const navItems = [
    { name: 'Home', path: '/' },
    { name: 'AI Analysis', path: '/ai-analysis' },
    { name: 'About', path: '/about' },
    { name: 'Contact', path: '/contact' },
  ];

  const toggleMenu = () => setIsMenuOpen(!isMenuOpen);

  return (
    <header
      className={`sticky top-0 z-40 transition-all duration-300 ${
        scrolled
          ? 'bg-white/80 backdrop-blur-xl shadow-glass border-b border-gray-100/50'
          : 'bg-white/95 backdrop-blur-sm'
      }`}
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between items-center h-16 md:h-20">
          {/* Logo */}
          <Link to="/" className="flex items-center space-x-2 sm:space-x-3 group">
            <img
              src="/logo.png"
              alt="Suitcase Muse Logo"
              className="w-10 h-10 sm:w-12 sm:h-12 object-contain transition-transform duration-300 group-hover:scale-105"
            />
            <div>
              <span className="block font-nunito font-bold text-lg sm:text-xl text-charcoal">Suitcase Muse</span>
              <p className="text-xs text-gray-500 -mt-0.5 hidden sm:block">Styling Experts</p>
            </div>
          </Link>

          {/* Desktop Navigation */}
          <nav className="hidden lg:flex items-center space-x-1 xl:space-x-2">
            <Link
              to="/"
              className="animated-underline text-charcoal hover:text-coral transition-colors duration-200 font-medium text-sm xl:text-base px-3 py-2 rounded-lg hover:bg-coral/5"
            >
              Home
            </Link>

            {/* Services mega menu trigger */}
            <div
              className="relative"
              onMouseEnter={openMega}
              onMouseLeave={scheduleCloseMega}
            >
              <button
                type="button"
                aria-haspopup="true"
                aria-expanded={megaOpen}
                onFocus={openMega}
                onBlur={scheduleCloseMega}
                className="inline-flex items-center gap-1 text-charcoal hover:text-coral transition-colors duration-200 font-medium text-sm xl:text-base px-3 py-2 rounded-lg hover:bg-coral/5"
              >
                Services
                <ChevronDown
                  size={14}
                  className={`transition-transform duration-200 ${megaOpen ? 'rotate-180' : ''}`}
                />
              </button>

              {/* Mega menu panel */}
              <div
                className={`absolute left-1/2 -translate-x-1/2 top-full mt-3 w-[760px] max-w-[92vw] transition-all duration-200 ${
                  megaOpen ? 'opacity-100 translate-y-0 visible' : 'opacity-0 -translate-y-2 invisible'
                }`}
              >
                <div
                  className="bg-white/95 backdrop-blur-xl border border-gray-100 rounded-3xl shadow-glass-lg p-5"
                  onMouseEnter={openMega}
                  onMouseLeave={scheduleCloseMega}
                >
                  <div className="grid grid-cols-2 gap-3">
                    {SERVICE_ITEMS.map((s) => (
                      <Link
                        key={s.path}
                        to={s.path}
                        onClick={() => setMegaOpen(false)}
                        className="group flex items-center gap-4 p-3 rounded-2xl hover:bg-coral/5 transition-colors"
                      >
                        <div
                          className="flex-shrink-0 w-20 h-20 rounded-2xl bg-cover bg-center shadow-glass"
                          style={{ backgroundImage: `url("${s.image}")` }}
                          aria-hidden
                        />
                        <div className="min-w-0">
                          <div className="font-nunito font-bold text-charcoal group-hover:text-coral transition-colors">
                            {s.title}
                          </div>
                          <div className="text-xs text-gray-500 mt-0.5 leading-snug">{s.tagline}</div>
                        </div>
                      </Link>
                    ))}
                  </div>
                  <div className="mt-4 pt-4 border-t border-gray-100 flex items-center justify-between">
                    <span className="text-xs text-gray-500">All services at a glance</span>
                    <Link
                      to="/services"
                      onClick={() => setMegaOpen(false)}
                      className="inline-flex items-center gap-1.5 text-sm font-semibold text-coral hover:text-coral/80 transition-colors"
                    >
                      View pricing & full list
                      <ArrowRight size={14} />
                    </Link>
                  </div>
                </div>
              </div>
            </div>

            {navItems
              .filter((n) => n.name !== 'Home')
              .map((item) => (
                <Link
                  key={item.name}
                  to={item.path}
                  className="animated-underline text-charcoal hover:text-coral transition-colors duration-200 font-medium text-sm xl:text-base px-3 py-2 rounded-lg hover:bg-coral/5"
                >
                  {item.name}
                </Link>
              ))}
          </nav>

          {/* Desktop CTA */}
          <div className="hidden lg:flex items-center space-x-3">
            <Link to="/app/login" className="text-sm font-medium text-charcoal hover:text-coral transition-colors px-3 py-2">
              Log In
            </Link>
            <Link
              to="/app"
              className="bg-gradient-to-r from-coral to-coral-light hover:from-coral/90 hover:to-coral-light/90 text-white rounded-xl text-sm xl:text-base px-5 xl:px-6 py-2.5 xl:py-3 font-semibold transition-all duration-300 hover:shadow-glow-coral hover:scale-[1.02]"
            >
              Get Styled Now
            </Link>
          </div>

          {/* Mobile menu button */}
          <button
            onClick={toggleMenu}
            className="lg:hidden p-2 rounded-xl text-charcoal hover:text-coral hover:bg-coral/5 transition-all duration-200"
            aria-label="Toggle menu"
          >
            {isMenuOpen ? <X className="w-5 h-5 sm:w-6 sm:h-6" /> : <Menu className="w-5 h-5 sm:w-6 sm:h-6" />}
          </button>
        </div>

        {/* Mobile Navigation */}
        {isMenuOpen && (
          <div className="lg:hidden border-t border-gray-100 animate-fade-in max-h-[calc(100vh-4rem)] overflow-y-auto overscroll-contain py-4">
            <nav className="flex flex-col space-y-1 pb-6">
              <Link
                to="/"
                onClick={() => setIsMenuOpen(false)}
                className="text-charcoal hover:text-coral hover:bg-coral/5 transition-all duration-200 font-medium py-3 px-3 rounded-xl text-base"
              >
                Home
              </Link>

              {/* Mobile: Services with sub-items */}
              <div className="px-3 py-2 text-xs font-semibold uppercase tracking-wider text-gray-400 pt-3">
                Services
              </div>
              {SERVICE_ITEMS.map((s) => (
                <Link
                  key={s.path}
                  to={s.path}
                  onClick={() => setIsMenuOpen(false)}
                  className="flex items-center gap-3 py-2.5 px-3 rounded-xl hover:bg-coral/5"
                >
                  <div
                    className="w-12 h-12 rounded-xl bg-cover bg-center flex-shrink-0"
                    style={{ backgroundImage: `url("${s.image}")` }}
                    aria-hidden
                  />
                  <div>
                    <div className="font-medium text-charcoal text-sm">{s.title}</div>
                    <div className="text-xs text-gray-500">{s.tagline}</div>
                  </div>
                </Link>
              ))}
              <Link
                to="/services"
                onClick={() => setIsMenuOpen(false)}
                className="text-coral font-semibold py-2.5 px-3 rounded-xl text-sm hover:bg-coral/5"
              >
                View pricing & full list →
              </Link>

              <div className="px-3 py-2 text-xs font-semibold uppercase tracking-wider text-gray-400 pt-3">
                More
              </div>
              {navItems
                .filter((n) => n.name !== 'Home')
                .map((item) => (
                  <Link
                    key={item.name}
                    to={item.path}
                    className="text-charcoal hover:text-coral hover:bg-coral/5 transition-all duration-200 font-medium py-3 px-3 rounded-xl text-base"
                    onClick={() => setIsMenuOpen(false)}
                  >
                    {item.name}
                  </Link>
                ))}

              <div className="pt-4 space-y-2">
                <Link
                  to="/app/login"
                  className="block text-center text-charcoal border border-gray-200 rounded-xl w-full py-3 font-medium hover:bg-gray-50 transition-colors"
                  onClick={() => setIsMenuOpen(false)}
                >
                  Log In
                </Link>
                <Link
                  to="/app"
                  className="block bg-gradient-to-r from-coral to-coral-light text-white rounded-xl w-full text-center text-base py-3 font-semibold transition-all duration-300"
                  onClick={() => setIsMenuOpen(false)}
                >
                  Get Styled Now
                </Link>
              </div>
            </nav>
          </div>
        )}
      </div>
    </header>
  );
};

export default Header;
